import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CreditContext = createContext(null);

export const useCredits = () => useContext(CreditContext);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AI Credit cost map — mirrors backend core/ai_credits.py.
 * Keyed by the last segment of the AI endpoint path.
 */
const ENDPOINT_COSTS = {
  orchestrate: 1,
  "execute-tool": 1,
  "refine-problem": 1,
  "suggest-scope": 1,
  "suggest-ctqs": 1,
  "generate-smart-goal": 1,
  "autonomous-analyze": 5,
  analyze: 5,
  "root-cause-analysis": 5,
  "suggest-root-causes": 5,
  "analyze-solutions": 5,
  "doe-interpret": 5,
  "semantic-traceability": 5,
  "suggest-solutions": 5,
  "suggest-fmea": 5,
  "suggest-cost-benefit": 5,
  "suggest-pilot-plan": 5,
  "generate-pilot-plan": 5,
  "suggest-control-plan": 5,
  "suggest-kpis": 5,
  "suggest-financial-impact": 5,
  "suggest-data-collection-plan": 5,
  "review-measurement-system": 5,
  "suggest-baseline-metrics": 5,
  "guided-project-start": 5,
  "generate-executive-summary": 15,
  "generate-charter": 15,
  "closure-drafts": 15,
};

/** Return credit cost for a given AI endpoint URL. */
export function getCreditCost(url) {
  const segment = url.replace(/\?.*$/, "").split("/").filter(Boolean).pop();
  return ENDPOINT_COSTS[segment] ?? 1;
}

/**
 * CreditProvider — accepts `token` as a prop to avoid circular App.js import.
 * Also installs a global fetch interceptor for AI credit errors.
 */
export function CreditProvider({ token, children }) {
  const [credits, setCredits] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/user/credits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCredits(await res.json());
    } catch {
      /* silent */
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll every 60s to keep credits fresh
  useEffect(() => {
    if (!token) return;
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [token, refresh]);

  // Global listener: any component can dispatch this event to trigger the modal
  useEffect(() => {
    const handler = () => { setShowUpgradeModal(true); refresh(); };
    window.addEventListener("ai-credits-exhausted", handler);
    const refreshHandler = () => refresh();
    window.addEventListener("ai-credits-changed", refreshHandler);
    return () => {
      window.removeEventListener("ai-credits-exhausted", handler);
      window.removeEventListener("ai-credits-changed", refreshHandler);
    };
  }, [refresh]);

  const remaining = credits?.ai_credits_remaining ?? 0;
  const monthlyRemaining = credits?.monthly_credits_remaining ?? 0;
  const purchasedRemaining = credits?.purchased_credits_remaining ?? 0;
  const total = credits?.monthly_ai_credits ?? 1;
  const pct = total > 0 ? Math.round((monthlyRemaining / total) * 100) : 0;
  const isLow = pct > 0 && pct <= 20;
  const isExhausted = remaining <= 0 && credits !== null;

  return (
    <CreditContext.Provider
      value={{
        credits,
        remaining,
        monthlyRemaining,
        purchasedRemaining,
        total,
        pct,
        isLow,
        isExhausted,
        plan: credits?.plan ?? "free",
        resetDate: credits?.ai_credits_reset_date,
        refresh,
        showUpgradeModal,
        setShowUpgradeModal,
      }}
    >
      {children}
    </CreditContext.Provider>
  );
}
